/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * WHO IS AN IDENTITY-VERIFICATION PARTY (v1_649).
 *
 * The post-submit identity-verification / UBO list is report.sumsubRequired,
 * read by auto-actions (the link dispatch), client-read, the request-items
 * module and the review surfaces. It is (re)built from report.directors and
 * report.ubos in three places — the server IDV reconciliation (df-process), the
 * client documents-first merge (App.jsx dfCarryPeople) and the officer reprocess
 * merge (MLRODashboard carryPeopleForward). Each used to take EVERY entry with no
 * test of whether the person is actually a party of the ONBOARDING ENTITY.
 *
 * That listed people the documents merely NAMED but the board itself annotated
 * as not parties of the applicant — the VQF admission letter's own signatories
 * ("issuing authority signatory — not an officer of the client"), a director of
 * another company ("Block X Director, to be appointed at Completion"), and a
 * corporate shareholder listed as a UBO ("Block X Wealth Limited") — so eight
 * verification parties were raised where two were real (Sage Capital Management
 * AG, staging 16/09/26).
 *
 * The documents are still read and their names kept as context on the board.
 * This module is the single deterministic gate the three builders share: a
 * verification subject is a NATURAL PERSON who is a party of the applicant. A
 * name carrying an explicit not-a-party marker, and any non-natural person (a
 * corporate UBO), is context — never an identity-verification subject.
 * Instructions to the model are unreliable as compliance controls (the tax-gate
 * and email-gate doctrine); this is code, shared so the three paths agree.
 */
import { samePerson as canonicalSamePerson, sameEntryPerson } from './person-name-match.js';
import { caseOwnershipThreshold, DEFAULT_OWNERSHIP_THRESHOLD } from './ownership-threshold.js';
import { CLIENT_PERSON_ROLE } from './individual-case.js';

// Legal-entity markers. A director/UBO name carrying one of these is a company,
// not a natural person, so it is never an identity-verification subject. Mirrors
// the corporate-vs-natural heuristic used elsewhere for UBOs.
export const ENTITY_NAME = /\b(ltd|limited|plc|llp|llc|inc|incorporated|corp|corporation|company|gmbh|mbh|ag|sa|sarl|sas|bv|nv|oy|ab|aps|as|kft|sro|se|kg|ug|pte|pty|holdings?|group|trust|foundation|nominees?|partners|lp|kk|srl|spa)\b/i;

// Explicit not-a-party markers. These say, in the board's OWN words, that the
// person is not a director/UBO of the APPLICANT — the signatory of an issuing
// authority (a regulator/SRO admission letter), an officer of another named
// entity, or a contingent future appointee who is not yet in office. Kept tight
// so it never drops a genuine officer: "not a(n) <role> of/for ..." requires the
// "of/for" that points the negation at the applicant, so "not a director but a
// UBO" (still a party) is not caught.
//
// The last group is the STRUCTURALLY THIRD-PARTY functions — an auditor /
// statutory auditor / Revisionsstelle, and a notary. These are, by regulation,
// independent of the entity they act for: an auditor is never a director/UBO of
// the applicant and a notary who authenticates or signs is not a party either,
// so the marker is applicant-independent (no name comparison needed). The
// patterns are word-anchored so incidental text on a genuine officer never trips
// them — "audit committee" has no "auditor", and "notarised"/"notarially" carry
// no word boundary after "notar".
export const NON_PARTY_ROLE = new RegExp([
  'not\\s+(an?|the)\\s+(officer|director|board member|ubo|beneficial owner|shareholder|owner|party|signatory)s?\\s+(of|for|in|to)\\b',
  'not\\s+(a\\s+)?client\\s+officer',
  'issuing\\s+(authority|body)',
  'to\\s+be\\s+appointed',
  '\\bat\\s+completion\\b',
  '\\bupon\\s+completion\\b',
  'pursuant\\s+to\\s+clause',
  'prospective\\s+(director|officer|appointee)',
  'proposed\\s+(director|officer|appointee)',
  '\\bauditor(s)?\\b',
  '\\bstatutory\\s+auditor',
  '\\brevisionsstelle\\b',
  '\\baudit\\s+(firm|company|partner|expert|oversight)\\b',
  '\\bnotar(y|ies|in|innen|iat|e)?\\b',
  // v1_810: the company service functions that act FOR the applicant from
  // outside it. A registered agent, a formation agent, a corporate service
  // provider or a domiciliation provider is a third party by its function, as
  // an auditor is; its officers sign the applicant's certificates without being
  // the applicant's officers. These were on the df-process register list only
  // (NON_OFFICER_ROLE), so the resolver and the source gate kept them (Amy R.
  // Fountain, Bloxcross, production 25/09/26).
  '\\bregistered\\s+agents?\\b',
  '\\b(company\\s+)?formation\\s+agents?\\b',
  '\\bcorporate\\s+services?\\s+providers?\\b',
  '\\bdomiciliation\\s+(agent|provider|company|service|services)\\b',
  '\\baudit\\s+company\\b'
].join('|'), 'i');

// v1_810: FUNCTION HOLDERS who are not officers of the applicant but can still
// be a party of it (an outsourced AML officer, a compliance officer, an
// authorised signatory "outsourced ... signatory"). They are never promoted to
// directors from a register, but they are not removed from verification either
// (03/08/26). This and NON_PARTY_ROLE are the one list: df-process reads its
// register exclusion from isNotAnOfficerRole below, so the register build and
// the resolver can no longer drift apart (the registered agent entry was on one
// list only).
export const NON_OFFICER_FUNCTION = /aml officer|money laundering (reporting )?officer|\bmlro\b|compliance officer|gwg[- ]?verantwortlich|gwg[- ]?stellvertret|deputy aml|outsourced|external\/non-staff|non-staff|service provider|legal counsel|domiciliation/i;

// v1_810: A SIGNATORY WHO ONLY CERTIFIED A DOCUMENT. The register of members of
// Bloxcross named "Keith Bliss" with the role "Signatory certifying the register
// (role not stated in document)", and the certificate of incorporation named
// "Amy R. Fountain" as "signatory of the certificate". Certifying, attesting or
// witnessing a document gives a person no office in the company. The marker only
// counts when the role carries no applicant title at all, so a director who also
// certified a register ("Director; certified the register") is still a director.
// v1_821d: an apostille or notarial signatory is a certifying signatory too:
// "H. Nold, Signatory, Chancellery of State of the Canton of Grisons
// (apostille)" (STG AG) sat in directors with the verification lock. The
// apostille, the chancellery (Staatskanzlei) that issues it, a notary or notary
// public, and a legalisation certify a document; none gives an office.
const CERTIFYING = /\bapostill(e|es|ed)\b|\bchancell?(e)?ry\b|\bchancery\b|\bstaatskanzlei\b|\blegali[sz](ation|ations|ed|ing)\b|\bnotari(al|ally|sed|zed)\b|\bnotary(\s+public)?\b|\bsignatory\s+certifying\b|\bcertifying\s+(signatory|officer|person|party)\b|\bsignatory\s+of\s+the\s+(certificate|register|extract|copy|document)\b|\bcertif(ied|ying)\s+(the\s+|this\s+|a\s+)?(true\s+)?(copy|register|extract|document|certificate)\b|\battest(ing|ation)\s+(signatory|officer|witness)\b|\bwitness(es|ed)?\b/i;
const APPLICANT_TITLE = /\b(director|board member|member of the board|chair(man|person|woman)?|verwaltungsrat|pr(ä|ae)sident|president|managing partner|general partner|gesch(ä|ae)ftsf(ü|ue)hrer(in)?|company secretary|chief executive|ceo|cfo|coo|beneficial owner|ubo|shareholder|controlling person|person with significant control|psc)\b/i;
export function certifyingOnlyRole(role) {
  const r = String(role || '');
  return CERTIFYING.test(r) && !APPLICANT_TITLE.test(r);
}

// Strip a parenthetical alternate spelling / fuller-name aside before matching,
// so "Rupertus Rothenhäuser (Rupertus Rothenhaeuser)" on directors and
// "Rupertus Rothenhaeuser — resident Switzerland" on ubos resolve to ONE person.
const stripAside = (n) => String(n || '').replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();

// Fallback name key when no samePerson matcher is injected: lower-cased, German
// umlaut folded (ä→ae) so the two Rupertus spellings agree, parentheticals and
// punctuation removed.
const foldKey = (n) => stripAside(n).toLowerCase()
  .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z\s]/g, ' ').replace(/\s+/g, ' ').trim();

// v1_755: with no matcher injected, the canonical samePerson (the rule the
// interview escalations and case state use) decides, not a weaker built-in copy.
// Five callers (party screening on both sides, df-process, monitoring) pass none,
// so they missed "T W Ashcombe" against "Tobias Wren Ashcombe" (an initial for a
// first name) and surname first orderings, and listed one person twice.
const matcher = (samePerson) => {
  const same = (typeof samePerson === 'function') ? samePerson : canonicalSamePerson;
  return (a, c) => same(stripAside(a), stripAside(c));
};

// ── Garbled single-token variant merge (v1_678) ──────────────────────────────
// The structural name matcher (samePerson) merges a full
// name written two ways, but it cannot bridge a GARBLED single-word capture to
// the full legal name: "Ruperthus" vs "Rupertus Rothenhäuser" is one token (so
// the first+last rule cannot apply) and not an exact substring (the typo 'h'
// defeats containment), so the same director was counted twice and screened
// twice ("potential match: Ruperthus"). This adds a deliberately narrow,
// corroborated rule so that class of capture resolves to ONE party, while two
// genuinely different people never merge. It is only consulted when the
// structural matcher has ALREADY said "not the same".
const nameTokens = (n) => foldKey(n).split(' ').filter(Boolean);
function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = a[i - 1] === b[j - 1] ? prev[j - 1] : 1 + Math.min(prev[j - 1], prev[j], cur[j - 1]);
    }
    prev = cur;
  }
  return prev[n];
}
// Is the single garbled word `w` a close variant of the token `t`? A real
// substring (both long enough that it is not an incidental fragment) or a small
// typo distance scaled to length. Deliberately tight: too small a token or too
// large a distance is not a confident variant.
function closeTokenVariant(w, t) {
  if (!w || !t || w.length < 4 || t.length < 4) return false;
  if (w === t) return true;
  if ((t.includes(w) || w.includes(t)) && Math.min(w.length, t.length) >= 4) return true;
  const longer = Math.max(w.length, t.length);
  const tol = longer >= 8 ? 2 : (longer >= 5 ? 1 : 0);
  const d = editDistance(w, t);
  return d > 0 && d <= tol;
}
// Does the single-token name `w` closely match some token of the multi-token
// name `tokens`? Returns true only when the match is confident by closeTokenVariant.
const singleWordMatchesName = (w, tokens) => tokens.some(t => closeTokenVariant(w, t));

const entryName = (e) => (typeof e === 'string') ? e : (e && e.name) || '';
const entryField = (e, k) => (e && typeof e === 'object' && e[k] != null && e[k] !== '') ? e[k] : null;
// A role "family" token, so two "Director" roles corroborate but a Director and
// a UBO (which a single person legitimately can both be) are simply not treated
// as a positive role corroborator rather than a conflict.
const roleFamily = (role) => {
  const r = String(role || '').toLowerCase();
  if (/\bubo|beneficial owner\b/.test(r)) return 'ubo';
  if (/\bdirector|officer|board\b/.test(r)) return 'director';
  if (/\bsignatory|signator|prokur\b/.test(r)) return 'signatory';
  if (/\bshareholder|member\b/.test(r)) return 'shareholder';
  return '';
};
// Would merging these two entries CONFLICT on a distinguishing field? An explicit
// nationality, DOB or numeric ownership present on BOTH and disagreeing means
// they are different people — never merge, whatever the names look like.
function conflictsOnField(a, b) {
  const natA = entryField(a, 'nationality') || entryField(a, 'country') || entryField(a, 'residence');
  const natB = entryField(b, 'nationality') || entryField(b, 'country') || entryField(b, 'residence');
  if (natA && natB && foldKey(String(natA)) !== foldKey(String(natB))) return true;
  const dobA = entryField(a, 'dob') || entryField(a, 'dateOfBirth');
  const dobB = entryField(b, 'dob') || entryField(b, 'dateOfBirth');
  if (dobA && dobB && String(dobA).trim() !== String(dobB).trim()) return true;
  const owA = entryField(a, 'ownership'), owB = entryField(b, 'ownership');
  if (owA != null && owB != null) {
    const na = parseFloat(String(owA)), nb = parseFloat(String(owB));
    if (Number.isFinite(na) && Number.isFinite(nb) && Math.abs(na - nb) > 0.5) return true;
  }
  return false;
}
// At least one POSITIVE corroborator that these two entries are the same person.
function corroborates(a, b) {
  const natA = entryField(a, 'nationality') || entryField(a, 'country') || entryField(a, 'residence');
  const natB = entryField(b, 'nationality') || entryField(b, 'country') || entryField(b, 'residence');
  if (natA && natB && foldKey(String(natA)) === foldKey(String(natB))) return true;
  const dobA = entryField(a, 'dob') || entryField(a, 'dateOfBirth');
  const dobB = entryField(b, 'dob') || entryField(b, 'dateOfBirth');
  if (dobA && dobB && String(dobA).trim() === String(dobB).trim()) return true;
  const owA = entryField(a, 'ownership'), owB = entryField(b, 'ownership');
  if (owA != null && owB != null && parseFloat(String(owA)) === parseFloat(String(owB))) return true;
  const rfA = roleFamily(entryField(a, 'role')), rfB = roleFamily(entryField(b, 'role'));
  if (rfA && rfB && rfA === rfB) return true;
  return false;
}
// Confident garbled-variant merge between two ENTRIES (objects or strings), used
// only after the structural matcher returned false. One name must be a single
// word that is a close variant of exactly one token of the other (multi-token)
// name, they must not conflict on a distinguishing field, and at least one field
// must positively corroborate. Symmetric.
export function garbledVariantSame(a, b) {
  const na = entryName(a), nb = entryName(b);
  const ta = nameTokens(na), tb = nameTokens(nb);
  if (!ta.length || !tb.length) return false;
  let single, multi;
  if (ta.length === 1 && tb.length > 1) { single = ta[0]; multi = tb; }
  else if (tb.length === 1 && ta.length > 1) { single = tb[0]; multi = ta; }
  else return false; // only the garbled single-word-vs-full-name case
  if (!singleWordMatchesName(single, multi)) return false;
  if (conflictsOnField(a, b)) return false;
  return corroborates(a, b);
}
// The entry-aware "same party" predicate the resolvers dedup with: the structural
// name match first, then the corroborated garbled-variant fallback. `match` is the
// name-only matcher (injected samePerson or the folded default).
// v1_852: or one email address carried by both with a close spelling of the
// name (the chair as "Dr. Abdulla Al Nasser" and "Abdullah Al Nasser").
const samePartyEntry = (match, a, b) => match(entryName(a), entryName(b)) || sameEntryPerson(a, b, match) || garbledVariantSame(a, b);
// Prefer the fuller legal name when two spellings merge (more tokens wins, then
// the longer string), so a garbled single word never becomes the surviving name.
const fullerEntry = (a, b) => {
  const ta = nameTokens(entryName(a)).length, tb = nameTokens(entryName(b)).length;
  if (ta !== tb) return ta > tb ? a : b;
  return entryName(a).length >= entryName(b).length ? a : b;
};

// Company-name key: folded like foldKey, with the legal-form suffix words
// stripped so "JayBee AG" and "JayBee" compare equal and so an entity named in a
// role can be tested against the applicant's own name. Used only to decide
// whether an entity a role attributes a person to IS the applicant.
const CO_KEY_STRIP = /\b(ag|gmbh|mbh|ltd|limited|plc|llc|inc|incorporated|corp|corporation|co|sa|sarl|sas|bv|nv|oy|ab|aps|se|kg|kgaa|ug|pte|pty|srl|spa|llp|lp|holdings?|holding|group|partners|stiftung|anstalt|foundation|trust)\b/g;
const coKey = (n) => stripAside(n).toLowerCase()
  .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(CO_KEY_STRIP, ' ')
  .replace(/\s+/g, ' ').trim();

// The applicant's own names, from the board — the client/legal/company names the
// board carries. resolveDirectors/resolveUbos/resolveVerificationParties derive
// this from the board they are given and pass it to the party gate; the client
// merges (App.jsx dfCarryPeople, MLRODashboard carryPeopleForward) derive it the
// same way so every path agrees on who the applicant is.
export function applicantNamesFromBoard(board) {
  const b = board && typeof board === 'object' ? board : {};
  const cd = (b.companyDetails && typeof b.companyDetails === 'object') ? b.companyDetails : {};
  return [b.clientName, b.companyName, cd.legalName, cd.registeredName, cd.name]
    .map(v => (typeof v === 'string' ? v.trim() : '')).filter(Boolean);
}

// Officer/signatory title words, and the connectors that attribute such a title
// to a NAMED legal entity ("Managing Director for JayBee AG", "Managing
// Director, JayBee AG", "Signatory on behalf of Block X AG").
const OFFICER_ROLE = '(?:managing\\s+director|deputy\\s+managing\\s+director|director|officer|managing\\s+partner|general\\s+partner|partner|company\\s+secretary|secretary|authorised\\s+signatory|authorized\\s+signatory|signatory|representative|proxy|prokuristin|prokurist|gesch(?:ä|ae)ftsf(?:ü|ue)hrer(?:in)?|ceo|cfo|coo|board\\s+member|managing\\s+member|chair(?:man|person|woman)?)';
const ATTRIB_CONNECTOR = '(?:\\s*[,–—/-]\\s*|\\s+(?:of|for|at|with|to|on\\s+behalf\\s+of|der|des|für|fuer|von|vom|bei)\\s+)';
// The attributed entity: a token starting a name, then up to a short run within
// the same clause, ending in a corporate suffix. Lazy and punctuation-bounded so
// it captures just the entity name.
// v1_810: the suffix may follow a comma, the way US companies are written
// ("Harvard Business Services, Inc."). Without it the comma ended the name
// before its suffix and the attribution never matched.
const CO_ENTITY = '([A-Za-z0-9ÄÖÜäöüß][^,;–—]{0,60}?(?:,\\s*)?\\b(?:ag|gmbh|mbh|ltd|limited|plc|llc|inc|incorporated|corp|corporation|sa|sàrl|sarl|sas|bv|nv|oy|ab|aps|se|kg|kgaa|ug|pte|pty|srl|spa|llp|lp|holdings?|holding|group|partners|stiftung|anstalt|foundation|trust))\\b';
const ATTRIB_RE = new RegExp('\\b' + OFFICER_ROLE + ATTRIB_CONNECTOR + CO_ENTITY, 'i');

// Does this role attribute the person to a NAMED entity that is NOT the applicant
// — the case the plain NON_PARTY_ROLE markers miss? "Managing Director for JayBee
// AG" (the AML outsourcing provider), "Signatory on behalf of Block X AG". Kept
// tight so it never drops a genuine officer of the applicant:
//   • with no applicant name to compare against, it never fires;
//   • if any applicant name appears anywhere in the role, the person is tied to
//     the applicant and is kept (a person who is BOTH the applicant's director
//     and an officer of another company is still the applicant's director);
//   • it drops only when the entity the title is attributed to is a different
//     named company from the applicant.
function foreignEntityAttribution(role, applicantNames) {
  const r = String(role || '');
  if (!r) return false;
  const appList = (Array.isArray(applicantNames) ? applicantNames : (applicantNames ? [applicantNames] : []));
  const appKeys = appList.map(coKey).filter(k => k && k.length > 1);
  if (!appKeys.length) return false;                       // no applicant to compare → never drop
  // The applicant named anywhere in the role keeps the person, whatever else the
  // role attributes them to.
  const rk = foldKey(r);
  for (const a of appList) {
    const ak = foldKey(a);
    if (ak && ak.length > 3 && rk.includes(ak)) return false;
  }
  const re = new RegExp(ATTRIB_RE.source, 'gi');
  let m;
  while ((m = re.exec(r))) {
    const ek = coKey(m[1]);
    if (!ek) continue;
    const isApplicant = appKeys.some(a => a === ek || a.includes(ek) || ek.includes(a));
    if (!isApplicant) return true;                         // attributed to a foreign named entity
  }
  return false;
}

// A verification subject must be a natural person. A UBO flagged corporate, or
// any director/UBO whose name reads as a legal entity, is not (unless the board
// explicitly flagged it a natural person via corporateUBO === false).
export function isNaturalPerson(entry) {
  if (!entry) return false;
  if (entry.corporateUBO === true) return false;
  const name = (typeof entry === 'string') ? entry : (entry.name || '');
  if (!name || !foldKey(name)) return false;
  if (entry.corporateUBO !== false && ENTITY_NAME.test(stripAside(name))) return false;
  return true;
}

// SRO / REGULATOR SIGNATORY (v1_669). The v1_667 markers only caught a role that
// SPELLED OUT "issuing authority" / "not an officer of ...". But the assemble
// model labels an issuing-authority signatory with a plausible title and a bare
// body-plus-letter aside — "Head of Finance & Administration, VQF (signatory of
// admission letter)", "Legal & Compliance, VQF (signatory of auxiliary-persons
// approval letter)" — which carries NO such marker, so the two VQF officials who
// signed Sage Capital's membership letter reached directors, sumsubRequired and
// the interview brief again (real board 65c8a17c, staging 18/09/26). "VQF" has no
// corporate suffix so foreignEntityAttribution missed it too.
//
// A person is the ISSUING AUTHORITY's signatory — not an officer of the applicant
// — when their role names a self-regulatory organisation or financial regulator
// AND names a document THAT BODY ISSUES (an admission/approval/membership letter,
// an auxiliary-persons approval). BOTH are required, so this never fires on a
// bare title, on a bare regulator mention (an "FCA-approved director of the
// applicant" is kept), or on a bare "signatory" (an "authorised signatory of a
// VQF-regulated firm" is kept): only the body-issues-a-document combination — the
// actual phantom pattern — drops the person. A genuine director is never dropped.
const SRO_BODY = /\b(vqf|finma|polyreg|so[-\s]?fit|arif|oar[-\s]?g|sro|self[-\s]?regulator|fca|\bpra\b|\bsec\b|bafin|\bfma\b|cssf|mfsa|cima|\bfsc\b|\bfsa\b|vara|\bmas\b|\bsfc\b|\bamf\b|consob|dfsa|fsra|finra|esma|amla)\b/i;
const ISSUER_DOC = /\b(admission letter|letter of admission|admission decision|approval letter|letter of approval|approval decision|membership letter|membership confirmation|confirmation of membership|membership certificate|auxiliary[-\s]persons?|issuing authorit|issuing body)\b/i;
function sroSignatory(role) {
  const r = String(role || '');
  if (!r) return false;
  return SRO_BODY.test(r) && ISSUER_DOC.test(r);
}

// Does this role string mark the person as NOT a party of the applicant? An
// explicit not-a-party / third-party-function marker, a signatory/issuer of a
// named SRO's own document, OR a title attributed to a named entity other than
// the applicant (needs the applicant names to compare; omit them and only the
// applicant-independent markers apply).
export function isNonPartyRole(role, applicantNames = []) {
  const r = String(role || '');
  if (NON_PARTY_ROLE.test(r)) return true;
  if (sroSignatory(r)) return true;
  if (certifyingOnlyRole(r)) return true;
  return foreignEntityAttribution(r, applicantNames);
}

// v1_810: is this role NOT an officer of the applicant? A non party, or a
// function holder. The df-process register build uses this, so the two lists
// are read from one place.
export function isNotAnOfficerRole(role, applicantNames = []) {
  return NON_OFFICER_FUNCTION.test(String(role || '')) || isNonPartyRole(role, applicantNames);
}

// Why the role makes the person a non party, in words for the officer's note.
// No hyphens or dashes: this is product text.
export function nonPartyReason(role, applicantNames = []) {
  const r = String(role || '');
  if (/registered\s+agent|formation\s+agent|corporate\s+services?\s+provider|domiciliation\s+(agent|provider|company|service)/i.test(r)) return 'an officer of the company service provider (the registered agent), not of the company';
  if (foreignEntityAttribution(r, applicantNames)) return 'an officer of another company named in the role, not of the company, who signed a document for it';
  if (certifyingOnlyRole(r)) return 'a signatory who certified or witnessed a document, which gives no office in the company';
  if (sroSignatory(r)) return 'a signatory of the issuing authority, not an officer of the company';
  return 'not a party of the company by the role the documents give';
}

// v1_810: A SHAREHOLDER BELOW THE THRESHOLD, AND NOTHING ELSE. The stated share
// of an entry: its ownership, or for a verification list entry a percentage in
// a shareholder role ("Shareholder 5.61%"). Null when no share is stated: an
// unknown share is not a small share, so it never counts as below.
export function statedShare(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const own = entry.ownership;
  if (own != null && String(own).trim() !== '') {
    const m = String(own).match(/(\d+(?:\.\d+)?)/);
    const n = m ? Number(m[1]) : NaN;
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
  }
  const role = String(entry.role || '');
  if (/\b(shareholder|holder|member|holding|share)\b/i.test(role)) {
    const m = role.match(/(\d+(?:\.\d+)?)\s*(%|percent|per cent)/i);
    if (m) { const n = Number(m[1]); if (Number.isFinite(n) && n >= 0 && n <= 100) return n; }
  }
  return null;
}
// True only for a person whose sole basis on the file is a shareholding with a
// stated share below the threshold: no office, no signing authority, no control
// by other means, not the person completing the application. Such a person is
// not a beneficial owner, so not an identity verification subject (Shareholder 3
// at 5.61 percent, Bloxcross, production 25/09/26).
const OTHER_BASIS = /director|officer|signator|board|secretary|control|person completing|establishing|applicant|authoris|authoriz|trustee|settlor|protector|beneficiary|partner|pr(ä|ae)sident|gesch(ä|ae)ftsf/i;
export function subThresholdHolderOnly(entry, threshold = 25) {
  const share = statedShare(entry);
  if (share == null || share >= threshold) return false;
  // The role, and any field a board uses to say why a person is on the UBO list
  // (control by other means, a trust role), so a holder below 25 percent who
  // controls the applicant another way keeps their verification.
  const why = ['role', 'basis', 'control', 'controlType', 'controlBasis', 'natureOfControl', 'notes', 'reason']
    .map(k => (entry && typeof entry[k] === 'string') ? entry[k] : '').join(' ');
  return !OTHER_BASIS.test(why);
}
// The identity verification list's own test: a verification subject whose only
// basis is not a sub threshold shareholding.
// The verification list stores the role the resolver built: the list label, then
// the board role in brackets ("Director (Signatory certifying the register)") or
// after a dash (App.jsx). The label is the resolver's own word, not the
// document's, so the test reads the board role inside it.
export function sourceRole(role) {
  const r = String(role || '').trim();
  const m = r.match(/^(?:director|ubo|authori[sz]ed signatory)\s*(?:\((.*)\)|[\u2014\u2013-]\s*(.*))$/i);
  return m ? (m[1] != null ? m[1] : m[2]) : r;
}
export function isVerificationListEntry(entry, applicantNames = [], threshold = 25) {
  if (!isVerificationSubject(entry, applicantNames) || subThresholdHolderOnly(entry, threshold)) return false;
  const inner = sourceRole(entry && typeof entry === 'object' ? entry.role : '');
  return !(inner && isNonPartyRole(inner, applicantNames));
}

// The single predicate the three builders share: is this board entry an
// identity-verification subject — a natural person who is a party of the
// applicant? applicantNames lets the gate drop a person whose role attributes
// them to another named entity (e.g. "Managing Director for JayBee AG").
export function isVerificationSubject(entry, applicantNames = []) {
  return isNaturalPerson(entry) && !isNonPartyRole(entry && typeof entry === 'object' ? entry.role : '', applicantNames);
}

// v1_700: a person recorded as a beneficial owner or director on the file is a
// MANDATORY identity-verification subject — a fixed regulatory requirement whose
// verification cannot be waived. This predicate gates the officer's "remove from
// verification" controls: removing such a party is refused; taking them off the
// file is permitted ONLY as an explicit correction that they are NOT actually an
// officer (a mis-extraction, e.g. a notary the extractor conflated with a
// director), never as a discretionary "not applicable". The name matcher (the
// dashboard's samePerson) is injected so this stays pure and harnessable; without
// one it falls back to a normalised case-insensitive equality.
export function isConfirmedOfficerParty(name, board, samePerson) {
  if (!name || !board) return false;
  const eq = typeof samePerson === 'function'
    ? samePerson
    : (a, b) => String(a || '').toLowerCase().trim() === String(b || '').toLowerCase().trim();
  // v1_810: only a RESOLVED officer or beneficial owner is locked. The raw lists
  // can hold context the board keeps from the documents (a certifying signatory,
  // the registered agent's officer, a sub threshold holder), and the lock read
  // them raw, so Keith Bliss ("Signatory certifying the register") and Amy R.
  // Fountain (the registered agent's Assistant Secretary) showed "Verification
  // required, cannot be waived" (Bloxcross, production 25/09/26).
  const apps = applicantNamesFromBoard(board);
  const inList = (list, isUbo) => (Array.isArray(list) ? list : []).some(p => p && p.name && eq(p.name, name)
    && !isNonPartyRole(p.role, apps) && !(isUbo && subThresholdHolderOnly(p, caseOwnershipThreshold(board))));
  return inList(board.ubos, true) || inList(board.directors, false);
}

// The applicant's RESOLVED directors — the raw director entries that are
// verification subjects (natural-person officers of the applicant). Document-
// sourced non-parties (issuing-authority signatories, an officer of another
// entity, a contingent future appointee) are dropped; the entries are returned
// intact so callers keep role, email and any other field for display.
export function resolveDirectors(board, samePerson) {
  const apps = applicantNamesFromBoard(board);
  const match = matcher(samePerson);
  const subjects = (Array.isArray(board && board.directors) ? board.directors : []).filter(e => isVerificationSubject(e, apps));
  return dedupeParties(subjects, match);
}

// Dedupe a list of party entries by the entry-aware same-party predicate, keeping
// the fuller legal name when a garbled single-word spelling merges into it. A
// structural name match always merges. A garbled-variant match merges ONLY when
// it is unambiguous — a single word that closely matches TWO different kept
// entries is left separate rather than collapsed onto the wrong person.
function dedupeParties(entries, match) {
  const out = [];
  for (const e of entries) {
    const name = entryName(e);
    if (!name || !foldKey(name)) continue;
    const structural = out.findIndex(x => match(entryName(x), name) || sameEntryPerson(x, e, match));
    if (structural >= 0) { out[structural] = fullerEntry(out[structural], e); continue; }
    const garbled = out.filter(x => garbledVariantSame(x, e));
    if (garbled.length === 1) {
      const idx = out.indexOf(garbled[0]);
      out[idx] = fullerEntry(out[idx], e);
      continue;
    }
    // No match, or an ambiguous garbled match (would collapse two people) → keep separate.
    out.push(e);
  }
  return out;
}

// The applicant's RESOLVED beneficial owners — natural-person UBOs of the
// applicant, deduped. A non-natural person (a corporate UBO / entity) is not a
// beneficial owner in its own right and is dropped (its natural-person owners
// appear via the ownership chain). Deduped with the injected `samePerson`
// matcher, falling back to a folded name key, so one person written two ways
// (e.g. "Nathan Sage (resident UK)" and "Nathan Philip Sage") is a single entry.
// Entries are returned intact so ownership, source and SoW fields survive.
export function resolveUbos(board, samePerson) {
  const list = (Array.isArray(board && board.ubos) ? board.ubos : []);
  const apps = applicantNamesFromBoard(board);
  const match = matcher(samePerson);
  // v1_817: a UBO entry with a stated share below the threshold and no other
  // basis is a shareholder (R5, as the identity list already reads it). The
  // column listed five Bloxcross holders the officer had set below 25 percent
  // as beneficial owners while the header counted them as shareholders.
  return dedupeParties(list.filter(u => isVerificationSubject(u, apps) && !subThresholdHolderOnly(u, caseOwnershipThreshold(board))), match);
}

// v1_817: R5 ON THE STORED BOARD, ON EVERY PATH. A UBO entry whose only basis is
// a stated share below the threshold moves to board.shareholders, keeping its
// figure and, where the officer recorded it, the source, so the figure survives
// a rebuild (the next register leg with no share reads it back). Already held
// shareholders are merged by person, an officer recorded figure winning. Pure
// apart from the board it is given; returns the names moved.
export function settleSubThresholdHolders(board, samePerson, threshold = caseOwnershipThreshold(board), opts = {}) {
  if (!board || typeof board !== 'object') return [];
  const match = matcher(samePerson);
  const ubos = Array.isArray(board.ubos) ? board.ubos : [];
  const moving = ubos.filter(u => u && typeof u === 'object' && u.name && subThresholdHolderOnly(u, threshold));
  const stored = (Array.isArray(board.shareholders) ? board.shareholders : []).filter(h => h && h.name);
  // v1_830: the other direction. A stored shareholder whose stated share is at
  // or above the case's threshold (the firm lowered it, say from 25 to 10) is a
  // beneficial owner: back onto the UBO list with its figure and source. Only
  // the holders a lowered threshold newly covers: a natural person, a stated
  // share below 25 (anything at or above 25 on this list was not put there by
  // the threshold, so it is not ours to move), not marked uncertain, and not a
  // person the officer removed (opts.removed, the partyRemovals names).
  const removed = (Array.isArray(opts.removed) ? opts.removed : []).map(x => (x && typeof x === 'object') ? x.name : x).filter(Boolean);
  const rising = stored.filter(h => {
    const n = statedShare(h);
    return n != null && n >= threshold && n < DEFAULT_OWNERSHIP_THRESHOLD && !h.uncertain && isNaturalPerson(h) && !removed.some(r => match(r, h.name));
  });
  const held = stored.filter(h => !rising.includes(h));
  if (rising.length) {
    const nextUbos = ubos.filter(u => !moving.includes(u));
    for (const h of rising) {
      if (nextUbos.some(u => u && u.name && match(u.name, h.name))) continue;
      nextUbos.push({
        name: h.name,
        ownership: String(statedShare(h)) + '%',
        role: 'UBO',
        ...(h.ownershipSource ? { ownershipSource: h.ownershipSource } : {}),
        ...(h.nationality ? { nationality: h.nationality } : {}),
        ...(h.countryOfResidence ? { countryOfResidence: h.countryOfResidence } : {}),
      });
    }
    board.ubos = [...nextUbos, ...moving];
  }
  if (!moving.length) { if (held.length || Array.isArray(board.shareholders)) board.shareholders = held; return []; }
  const out = [...held];
  for (const u of moving) {
    const entry = {
      name: u.name,
      ownership: String(statedShare(u)),
      ...(u.ownershipSource ? { ownershipSource: u.ownershipSource } : {}),
      source: u.ownershipSource ? 'share recorded by the officer' : (u.source || 'share recorded on the file'),
      ...(u.nationality ? { nationality: u.nationality } : {}),
      ...(u.countryOfResidence ? { countryOfResidence: u.countryOfResidence } : {}),
    };
    const i = out.findIndex(h => match(h.name, u.name));
    if (i < 0) out.push(entry);
    else if (entry.ownershipSource || !out[i].ownershipSource) out[i] = { ...out[i], ...entry };
  }
  board.ubos = (Array.isArray(board.ubos) ? board.ubos : ubos).filter(u => !moving.includes(u));
  board.shareholders = out;
  return moving.map(u => u.name);
}

/**
 * The identity-verification party list, derived from the RESOLVED board.
 *
 * Directors, UBOs and authorised signatories of the applicant, keeping a person
 * only when they are a verification subject. Deduped across the three lists (a
 * person who is both a director and a UBO appears once, director spelling first)
 * using the injected `samePerson` matcher, falling back to a folded name key.
 * Authorised signatories of the applicant are verification subjects in their own
 * right, so an excluded FUNCTION word in their role (e.g. "outsourced ...
 * signatory") does not remove them (03/08/26) — only an explicit not-a-party
 * marker or being a non-natural person does. Never invents a party.
 *
 * Returns [{ name, role, email }].
 */
// v1_755: the name kept for a merged person is the fuller one: more words, or on
// a tie the longer spelling ("Tobias Wren Ashcombe" over "T W Ashcombe").
const fuller = (a, b) => { const ta = nameTokens(a), tb = nameTokens(b); return ta.length > tb.length || (ta.length === tb.length && ta.join('').length > tb.join('').length); };
export function resolveVerificationParties(board, samePerson) {
  const b = board || {};
  const apps = applicantNamesFromBoard(b);
  const match = matcher(samePerson);
  const out = [];
  const add = (entry, prefix) => {
    const name = (typeof entry === 'string') ? entry : (entry && entry.name);
    if (!name || !foldKey(name)) return;
    const src = (typeof entry === 'string') ? { name } : entry;
    // Dedup: a structural name match, or the corroborated garbled-variant merge
    // checked against the ORIGINAL source entry (which carries nationality /
    // ownership for the conflict guard), never the stripped display object.
    // v1_888: every other name form the person was listed under is kept
    // (alsoListedAs), so a screening record stored under a merged away
    // spelling is still found and shown on the one person's card.
    const keepForms = (tgt, otherName) => {
      const prev = tgt.name;
      if (fuller(otherName, tgt.name)) tgt.name = otherName;
      const forms = [...(tgt.alsoListedAs || []), ...((src && src.alsoListedAs) || []), prev, otherName].filter(n => n && foldKey(n) !== foldKey(tgt.name));
      const seenForms = new Set(); tgt.alsoListedAs = forms.filter(n => { const k = foldKey(n); if (seenForms.has(k)) return false; seenForms.add(k); return true; });
      if (!tgt.alsoListedAs.length) delete tgt.alsoListedAs;
    };
    const structural = out.findIndex(y => match(y.name, name) || sameEntryPerson(y._src, src, match));
    if (structural >= 0) { keepForms(out[structural], name); return; }
    const garbled = out.filter(y => garbledVariantSame(y._src, src));
    if (garbled.length === 1) { keepForms(garbled[0], name); return; }
    if (garbled.length > 1) return; // ambiguous garbled match → do not add a possible duplicate onto the wrong person
    const extra = (entry && typeof entry === 'object' && entry.role) ? ' (' + entry.role + ')' : '';
    const own = (src && Array.isArray(src.alsoListedAs)) ? src.alsoListedAs.filter(n => n && foldKey(n) !== foldKey(name)) : [];
    out.push({ name, role: prefix + extra, email: (entry && typeof entry === 'object' && entry.email) || null, ...(own.length ? { alsoListedAs: [...new Set(own)] } : {}), _src: src });
  };
  // Individual onboarding (21/10/2026): an individual's client is the party
  // to verify and screen (the board lists no directors, owners or
  // signatories). Added first and directly: the client's name is also the
  // applicant's, so the applicant test below would drop them. Their date of
  // birth and nationality go with them so the screen can match on them.
  if (String(b.clientKind || '').toLowerCase() === 'individual') {
    const cp = (b.clientPerson && typeof b.clientPerson === 'object') ? b.clientPerson : {};
    const nm = String(cp.fullName || b.clientName || '').trim();
    if (nm && foldKey(nm)) {
      const nats = [cp.nationality, ...(Array.isArray(cp.otherNationalities) ? cp.otherNationalities : [])].map(x => String(x || '').trim()).filter(x => x && !/^[.\u2026]+$/.test(x));
      const dob = String(cp.dateOfBirth || '').trim();
      out.push({ name: nm, role: CLIENT_PERSON_ROLE, email: b.contactEmail || null, ...(/^\d{4}(-\d{2}(-\d{2})?)?$/.test(dob) ? { birthDate: dob } : {}), ...(nats.length ? { nationality: nats } : {}), _src: { name: nm } });
    }
  }
  for (const d of (Array.isArray(b.directors) ? b.directors : [])) {
    const e = (typeof d === 'string') ? { name: d } : d;
    if (!isVerificationSubject(e, apps)) continue;
    add(e, 'Director');
  }
  for (const u of (Array.isArray(b.ubos) ? b.ubos : [])) {
    if (!isVerificationSubject(u, apps)) continue; // a corporate UBO is context, never an IDV subject
    // v1_810: a UBO entry with a stated share below the threshold is a
    // shareholder, not a beneficial owner. A person who is also a director or
    // signatory is on the list from that entry; an unknown share stays in.
    if (subThresholdHolderOnly(u, caseOwnershipThreshold(b))) continue; // v1_830: the case's threshold
    add(u, 'UBO');
  }
  for (const a of (Array.isArray(b.authorisedSignatories) ? b.authorisedSignatories : [])) {
    const e = (typeof a === 'string') ? { name: a } : a;
    if (!isVerificationSubject(e, apps)) continue;
    add(e, 'Authorised signatory');
  }
  return out.map(({ _src, ...rest }) => rest); // drop the internal source ref
}
